import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import PdfViewer from '../../../components/shared/pdfViewer/PdfViewer';
import './ProcessingDocumentsEdit.scss';
import Breadcrumbs from '../../../components/shared/breadcrumbs/Breadcrumbs';
import ProcessingFields from '../../../components/shared/ProcessingFields/ProcessingFields';
import {
    useGetV1DocumentClassificationGetExtractedFields,
    usePostV1DocumentClassificationUpdateExtractedFields,
    useGetV1ProcessGetDocumentsProcessingInProcessProcessId,
} from '../../../api/generated/endpoint';
import {
    GetExtractedFieldsDTO,
    UpdateExtractedFieldsDTO,
} from '../../../api/generated/models';
import { ProcessingDocumentsResponse } from '../../../api/generated/models';
import { Loading } from '@appkit4/react-components';
import { FieldUpdateItem } from '../../../api/generated/models';
import useGenericErrorToast from '../../../helpers/useGenericErrorToast';
import useToast from '../../../helpers/useToast';
import useLocale from '../../../locale/useLocale';

const ProcessingDocumentsEdit = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { documentId, processingListingTab = 0 } = location.state || {};
    const [hasChanged, setHasChanged] = useState(false);

    const showToast = useToast();
    const { l } = useLocale();
    const showGenericErrorToast = useGenericErrorToast();

    const [breadcrumbItems, setBreadcrumbItems] = useState([
        {
            label: 'Process',
            href: '',
            processView: { listingTab: 0, selectedDocTypes: [] },
        },
        { label: 'Document' },
    ]);

    const activeTab = location.state?.activeTab || 0;
    const parentName = location.state?.parentName || '';

    const [processingData, setProcessingData] = useState<
        GetExtractedFieldsDTO[] | undefined
    >([]);

    const [sasUrl, setSasUrl] = useState<string | null>(null);
    const [processId, setProcessId] = useState<number | null>(null);
    const [documentName, setDocumentName] = useState<string>('');

    const [documents, setDocuments] = useState<ProcessingDocumentsResponse[]>(
        []
    );
    const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);

    const selectedDocTypes = location.state?.selectedDocTypes || [];

    const ValidationMapping: Record<string, number | number[]> = {
        'Awaiting Extraction': [2],
        'Awaiting Validation': [4, 9],
        'Validation Failed': [6],
        'Validation Low Confidence': [7],
        'Validation Passed': [8],
        'User Approved': [10],
        'PDF Generation Failed': [11],
        'PDF Generated': [12],
        'System Error': [1, 3, 5],
    };

    const getStatusByNumber = (num: number) => {
        for (const [key, value] of Object.entries(ValidationMapping)) {
            if (Array.isArray(value) ? value.includes(num) : value === num) {
                return key;
            }
        }
        return undefined;
    };

    const filterActionData = (data: ProcessingDocumentsResponse[]) => {
        return data.filter((row) => {
            const status = getStatusByNumber(row.processStatus || 0);
            return (
                status === 'System Error' ||
                status === 'Validation Low Confidence' ||
                status === 'Validation Failed'
            );
        });
    };

    const filterByDocType = (data: ProcessingDocumentsResponse[]) => {
        if (selectedDocTypes.length === 0) return data;
        return data.filter((item) =>
            selectedDocTypes.includes(item.documentType || '')
        );
    };

    const getFilteredDocuments = (data: ProcessingDocumentsResponse[]) => {
        const tabFiltered =
            processingListingTab === 1 ? filterActionData(data) : data;
        return filterByDocType(tabFiltered);
    };

    const { data, error } = useGetV1DocumentClassificationGetExtractedFields({
        DocumentId: documentId,
    });

    const updateClassification =
        usePostV1DocumentClassificationUpdateExtractedFields();

    const {
        data: processDocuments,
        error: processDocumentsError,
        refetch: refetchProcessDocuments,
    } = useGetV1ProcessGetDocumentsProcessingInProcessProcessId(
        Number(processId),
        undefined,
        {
            query: {
                enabled: processId !== null,
            },
        }
    );

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isProceeding, setIsProceeding] = useState(false);

    useEffect(() => {
        if (error) {
            console.error('API Error:', error);
            showGenericErrorToast(error);
        }
    }, [error, showGenericErrorToast]);

    useEffect(() => {
        if (processDocumentsError) {
            console.error('API Error:', processDocumentsError);
            showGenericErrorToast(processDocumentsError);
        }
    }, [processDocumentsError, showGenericErrorToast]);

    useEffect(() => {
        if (
            processDocuments &&
            Array.isArray(processDocuments.processingDocuments)
        ) {
            const filteredDocuments =
                processDocuments.processingDocuments.filter(
                    (doc) =>
                        doc.processStatus !== undefined &&
                        ![1, 2, 3, 4, 5, 9].includes(doc.processStatus)
                );

            const finalFilteredDocuments =
                getFilteredDocuments(filteredDocuments);
            setDocuments(finalFilteredDocuments);

            const index = finalFilteredDocuments.findIndex(
                (doc) => doc.documentId === parseInt(documentId)
            );
            setCurrentDocumentIndex(index !== -1 ? index : 0);
        }
    }, [processDocuments, documentId, processingListingTab, selectedDocTypes]);

    useEffect(() => {
        if (hasChanged == false) {
            if (data) {
                setProcessingData(
                    data.fields?.sort((a: any, b: any) => {
                        const orderA = a.orderNumber || 0;
                        const orderB = b.orderNumber || 0;
                        if (orderA === 0 && orderB === 0) {
                            return a.fieldId - b.fieldId;
                        }
                        if (orderA === 0) return -1;
                        if (orderB === 0) return 1;

                        return orderA - orderB;
                    }) || []
                );
                setSasUrl(data.sasUrl || null);
                setProcessId(data.processId || null);
                setDocumentName(data.documentName || '');

                // Update breadcrumbs with data from API
                setBreadcrumbItems([
                    {
                        label: data.processName || 'Process',
                        href: `/process/${data.processId}/document`,
                        processView: {
                            listingTab: location.state?.listingTab || 0,
                            selectedDocTypes:
                                location.state?.selectedDocTypes || [],
                        },
                    },
                    { label: data.documentName || 'Document' },
                ]);
            }
        }
    }, [data, location.state]);

    const handleFieldUpdate = (
        field: string,
        value: any,
        checkboxSelectedValues: any
    ) => {
        setHasChanged(true);
        setProcessingData((prev) => {
            if (!prev) return prev;
            if (checkboxSelectedValues) {
                return [
                    ...prev.map((item) =>
                        item.fieldName === field
                            ? {
                                  ...item,
                                  checkboxSelectedValues,
                              }
                            : item
                    ),
                ];
            }
            if (field === 'date' || field === 'documentType') {
                return [
                    ...prev.map((item) =>
                        item.fieldName === field
                            ? {
                                  ...item,
                                  value: Array.isArray(value)
                                      ? value
                                      : [
                                            ...(Array.isArray(item.value)
                                                ? item.value
                                                : []),
                                            value,
                                        ],
                              }
                            : item
                    ),
                ];
            }
            return [
                ...prev.map((item) =>
                    item.fieldName === field ? { ...item, value } : item
                ),
            ];
        });
    };

    const getNextDocument = async () => {
        if (documents.length === 0) return;
        const nextIndex = (currentDocumentIndex + 1) % documents.length;
        const nextDocument = documents[nextIndex];

        navigate(`/process/${processId}/document/${nextDocument.documentId}`, {
            state: {
                documentId: nextDocument.documentId,
                parentLocation: `/process/${processId}/document`,
                fileName: nextDocument.parentDocument,
                parentName: parentName,
                activeTab: activeTab,
                selectedDocTypes: selectedDocTypes,
                processingListingTab: processingListingTab,
            },
        });
    };

    const handleProceed = async () => {
        setIsSubmitting(true);
        const updatedFields: FieldUpdateItem[] = (processingData || []).map(
            (item) => ({
                fieldId: item.fieldId,
                newValue: Array.isArray(item.value)
                    ? item.value.join(', ')
                    : item.value,
                selectedCheckboxOptions: item.checkboxSelectedValues,
            })
        );

        const updatePayload: UpdateExtractedFieldsDTO = {
            documentId: parseInt(documentId),
            fieldUpdates: updatedFields,
        };
        try {
            await updateClassification.mutateAsync({ data: updatePayload });
            showToast(l('_success'), l('_fieldsUpdated'), 'success');
            setHasChanged(false);
            if (processId) {
                navigate(`/process/${processId}/document`, {
                    state: {
                        processingListingTab: processingListingTab,
                        selectedDocTypes: selectedDocTypes,
                    },
                });
            }
        } catch (error: any) {
            showGenericErrorToast(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleNext = async () => {
        setIsProceeding(true);
        const updatedFields: FieldUpdateItem[] = (processingData || []).map(
            (item) => ({
                fieldId: item.fieldId,
                newValue: Array.isArray(item.value)
                    ? item.value.join(', ')
                    : item.value,
                selectedCheckboxOptions: item.checkboxSelectedValues,
            })
        );
        const updatePayload: UpdateExtractedFieldsDTO = {
            documentId: parseInt(documentId),
            fieldUpdates: updatedFields,
        };
        try {
            await updateClassification.mutateAsync({ data: updatePayload });
            showToast(l('_success'), l('_fieldsUpdated'), 'success');
            setHasChanged(false);
            await refetchProcessDocuments();
            await new Promise((resolve) => setTimeout(resolve, 3000));
            getNextDocument();
        } catch (error: any) {
            showGenericErrorToast(error);
        } finally {
            setIsProceeding(false);
        }
    };

    return (
        <div className="processing-edit-container">
            <Breadcrumbs items={breadcrumbItems} />
            <div>
                <h2 className="ap-typography-heading-l title">
                    {documentName}
                    {data?.startPage &&
                        ` (extracted from page ${data?.startPage})`}
                </h2>
            </div>
            {sasUrl ? (
                <div className="processing-edit">
                    <div className="pdf-section">
                        <PdfViewer currentPage={1} fileUrl={sasUrl || ''} />
                    </div>
                    <div className="edit-section">
                        <ProcessingFields
                            onUpdate={handleFieldUpdate}
                            onProceed={handleProceed}
                            onNext={handleNext}
                            fields={processingData || []}
                            isSubmitting={isSubmitting}
                            isProceeding={isProceeding}
                            hasNextDocument={documents.length > 1}
                        />
                    </div>
                </div>
            ) : (
                <Loading
                    style={{ marginTop: '160px' }}
                    loadingType="circular"
                    indeterminate={true}
                    compact={false}
                ></Loading>
            )}
        </div>
    );
};

export default ProcessingDocumentsEdit;
